// src/components/operationCenter/widgets/token/TokenWidgetWrapper.tsx
// Provider nesting wrapper for token dashboard widgets.
// Wraps children in a synthetic TokenContext + whichever domain providers the card needs.

import React from "react";
import { TokenContext } from "@/contexts/tokenContextDef";
import { TokenPriceProvider } from "@/contexts/token/TokenPriceProvider";
import { TokenFundamentalsProvider } from "@/contexts/token/TokenFundamentalsProvider";
import { TokenDerivativesProvider } from "@/contexts/token/TokenDerivativesProvider";
import { WIDGET_EMPTY } from "../../WidgetWrapper";

export type TokenProviderType = "price" | "fundamentals" | "derivatives";

interface TokenWidgetWrapperProps {
  ticker: string;
  tokenId?: string;
  /** Which domain providers to mount. The synthetic TokenContext is always provided. */
  providers?: TokenProviderType[];
  children: React.ReactNode;
}

/**
 * Wraps children in a minimal synthetic TokenContext (avoids WebSocket / metadata fetch)
 * plus whichever domain providers the card actually needs.
 *
 * Provider nesting order (inner → outer):
 *   derivatives > fundamentals > price > synthetic TokenContext
 */
export const TokenWidgetWrapper: React.FC<TokenWidgetWrapperProps> = ({
  ticker,
  tokenId = "",
  providers = [],
  children,
}) => {
  if (!ticker) {
    return (
      <div className={WIDGET_EMPTY}>
        <div className="text-center space-y-2">
          <p>Enter a token ticker to load data</p>
          <p className="text-xs text-slate-600">Use the settings icon to configure</p>
        </div>
      </div>
    );
  }

  const upper = ticker.toUpperCase();

  // Build a minimal synthetic TokenContext value so cards that call useToken()
  // get a valid ticker without triggering WebSocket / full metadata fetch.
  const syntheticContextValue = {
    tokenId: tokenId || upper.toLowerCase(),
    ticker: upper,
    symbolForBinance: upper,
    name: upper,
    logoUrl: null,
    sectorName: null,
    displayPrice: null,
    price24hChange: null,
    snapshotPrice: null,
    ws24hChangeDecimal: null,
    marketCap: null,
    volume: null,
    tvl: null,
    fees: null,
    revenue: null,
    ratioMcTvl: null,
    ratioMcFees: null,
    ratioMcDau: null,
    ratioMcTx: null,
    ratioTvlToFees: null,
    ratioTvlToVol: null,
    changesByWindow: {},
    isLoading: false,
    isLoadingScreenerData: false,
    lastUpdated: null,
    useBackendPrice: true,
    priceData: null,
  };

  // Compose providers from innermost to outermost
  let content = children;

  if (providers.includes("derivatives")) {
    content = (
      <TokenDerivativesProvider ticker={upper}>
        {content}
      </TokenDerivativesProvider>
    );
  }

  if (providers.includes("fundamentals")) {
    content = (
      <TokenFundamentalsProvider ticker={upper}>
        {content}
      </TokenFundamentalsProvider>
    );
  }

  if (providers.includes("price")) {
    content = (
      <TokenPriceProvider ticker={upper}>
        {content}
      </TokenPriceProvider>
    );
  }

  return (
    <TokenContext.Provider value={syntheticContextValue}>
      {content}
    </TokenContext.Provider>
  );
};
